import {
  FontCategory,
  FontRole,
  fontById,
  fontStack,
  isFontFor,
  KEEPO_FONTS,
  KeepoFont,
} from "@keepoinc/site-page-react-component";
import { KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { catalogFontsUrl, isFreeFont, setStylesheet } from "../../../../../../utils/fonts";
import { Dropdown, Field, Option } from "./styles";

const prefix = "pages.editor.settings.form.fonts";
const CATEGORIES: FontCategory[] = ["sans", "serif", "display", "script"];

type PickerProps = {
  role: FontRole;
  value: string;
  onPick: (id: string) => void;
  /** Without PLUS: the fonts other than the free one ask to subscribe (`onLocked`) instead. */
  locked?: boolean;
  onLocked?: () => void;
};

/** A font picker: the font it has, written in itself, opening the fonts of its role by category. */
export const FontPicker = ({ role, value, onPick, locked, onLocked }: PickerProps) => {
  const { t } = useTranslation();
  const listId = useId();
  const container = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const label = t(`${prefix}.${role}`);
  const fonts = useMemo(() => KEEPO_FONTS.filter(font => isFontFor(font.id, role)), [role]);
  const groups = CATEGORIES.map(category => ({
    category,
    fonts: fonts.filter(font => font.category === category),
  })).filter(group => group.fonts.length);
  const selected = fontById(value) as KeepoFont;
  const isLocked = (font: KeepoFont) => !!locked && !isFreeFont(font.id, role);

  // every font of the catalog (only the letters of their names), to write the options in themselves
  useEffect(() => setStylesheet(document, "catalog", catalogFontsUrl()), []);

  // closes on a click outside it
  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  // the list takes the keys; the highlighted font stays in sight
  useEffect(() => {
    if (isOpen) list.current?.focus();
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) document.getElementById(`${listId}-${fonts[active]?.id}`)?.scrollIntoView?.({ block: "nearest" });
  }, [active, isOpen, fonts, listId]);

  const open = () => {
    setActive(
      Math.max(
        0,
        fonts.findIndex(font => font.id === value)
      )
    );
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    field.current?.focus();
  };

  const pick = (font: KeepoFont) => {
    if (isLocked(font)) {
      // the explanation comes up alone, the list closed behind it (the focus back at the field, for when it closes)
      close();
      onLocked?.();
      return;
    }
    onPick(font.id);
    close();
  };

  const keyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(Math.min(active + 1, fonts.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(active - 1, 0));
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (fonts[active]) pick(fonts[active]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <Field ref={container}>
      <span className="role" aria-hidden="true">
        {label}
      </span>
      <button
        ref={field}
        type="button"
        className="value"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label}: ${selected.name}`}
        onClick={() => (isOpen ? setOpen(false) : open())}>
        <span className="name" style={{ fontFamily: fontStack(selected.id, role) }}>
          {selected.name}
        </span>
      </button>
      {isOpen && (
        <Dropdown
          ref={list}
          id={listId}
          role="listbox"
          aria-label={label}
          tabIndex={-1}
          aria-activedescendant={fonts[active] ? `${listId}-${fonts[active].id}` : undefined}
          onKeyDown={keyDown}>
          {groups.map(group => (
            <div key={group.category} role="group" aria-label={t(`${prefix}.categories.${group.category}`)}>
              <span className="category" aria-hidden="true">
                {t(`${prefix}.categories.${group.category}`)}
              </span>
              <ul role="presentation">
                {group.fonts.map(font => (
                  <Option
                    key={font.id}
                    id={`${listId}-${font.id}`}
                    role="option"
                    aria-selected={font.id === value}
                    $active={fonts[active]?.id === font.id}
                    $locked={isLocked(font)}
                    style={{ fontFamily: fontStack(font.id, "heading") }}
                    onMouseEnter={() => setActive(fonts.indexOf(font))}
                    onClick={() => pick(font)}>
                    {font.name}
                  </Option>
                ))}
              </ul>
            </div>
          ))}
        </Dropdown>
      )}
    </Field>
  );
};
